import { ORDER_TRACKING_BILLING_DEFAULTS } from "@redotech/redo-billing-common/billing-defaults";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import {
  BillingPeriod,
  billingPeriodToFriendlyUpper,
} from "@redotech/redo-billing-common/service-invoice";
import { teamGetPaymentMethod } from "@redotech/redo-team-common/team";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { DateInput } from "@redotech/redo-web/date-picker";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { FormRadioGroup } from "@redotech/redo-web/radio";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import {
  areDatesEqual,
  getDateStartOfDayInTimezone,
} from "@redotech/util/date";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useEffect, useMemo } from "react";
import { useHasMarketingPermission } from "../../../marketing/use-marketing-permissions";
import { TeamContext } from "../../team";

export const orderTrackingBillingForm = groupInput({
  invoiceBilling: groupInput(
    {
      enabled: input<boolean>(),
      paymentProvider: input<PaymentProviderType>(),
      renewOnPeriodEnd: input<boolean>(),
      startDate: input<Date | null>({ equal: areDatesEqual }),
      period: input<BillingPeriod>(),
      periodFeeUsd: input<number>(),
      perOrderSettings: groupInput({
        enabled: input<boolean>(),
        includedOrders: input<number>(),
        overagePricePerOrderUsd: input<number>(),
      }),
      perShipmentSettings: groupInput({
        enabled: input<boolean>(),
        includedShipments: input<number>(),
        overagePricePerShipmentUsd: input<number>(),
      }),
      perSmsSettings: groupInput({
        enabled: input<boolean>(),
        includedCredits: input<number>(),
        overagePricePerSmsUsd: input<number>(),
        overagePricePerMmsUsd: input<number>(),
        chargeCarrierFees: input<boolean>(),
      }),
      perEmailSettings: groupInput({
        enabled: input<boolean>(),
        includedEmails: input<number>(),
        overagePricePerEmailUsd: input<number>(),
      }),
      /**
       * This has to be a separate field because evaluating it requires
       * knowing the team's timezone, which we don't have in the form validator.
       */
      startDateIsUpdatedToThePast: input<boolean>(),
    },
    {
      validator: (value) => {
        if (value.enabled && !value.startDate) {
          return ["Start date is required when invoice billing is enabled"];
        }
        if (value.startDateIsUpdatedToThePast) {
          return ["Start date cannot be in the past"];
        }
        return [];
      },
    },
  ),
  pricePerOrder: input<string>(),
  texts: groupInput({
    pricePerSms: input<string>(),
    pricePerMms: input<string>(),
  }),
});

export type OrderTrackingBillingForm = InputSchema.Input<
  typeof orderTrackingBillingForm
>;

export type OrderTrackingBillingValue = InputSchema.Value<
  typeof orderTrackingBillingForm
>;

export const orderTrackingBillingDefault: OrderTrackingBillingValue = {
  invoiceBilling: {
    enabled: false,
    paymentProvider: PaymentProviderType.SHOPIFY,
    renewOnPeriodEnd: true,
    startDate: null,
    period: BillingPeriod.MONTHLY,
    periodFeeUsd: ORDER_TRACKING_BILLING_DEFAULTS.PERIOD_FEE_USD,
    perOrderSettings: {
      enabled: false,
      includedOrders: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_ORDERS,
      overagePricePerOrderUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_ORDER_USD,
    },
    perShipmentSettings: {
      enabled: false,
      includedShipments: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_SHIPMENTS,
      overagePricePerShipmentUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SHIPMENT_USD,
    },
    perSmsSettings: {
      enabled: false,
      includedCredits: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_CREDITS,
      overagePricePerSmsUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SMS_USD,
      overagePricePerMmsUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_MMS_USD,
      chargeCarrierFees: false,
    },
    perEmailSettings: {
      enabled: false,
      includedEmails: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_EMAILS,
      overagePricePerEmailUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_EMAIL_USD,
    },
    startDateIsUpdatedToThePast: false,
  },
  pricePerOrder: (0.06).toString(),
  texts: { pricePerSms: (0.01).toString(), pricePerMms: (0.03).toString() },
};

export const OrderTrackingBillingCard = memo(function OrderTrackingBillingCard({
  input,
  orderTrackingEnabled,
}: {
  input: OrderTrackingBillingForm;
  orderTrackingEnabled: boolean;
}) {
  const { invoiceBilling } = input.inputs;
  const isAuthorized = useHasMarketingPermission("marketing_admin");
  const team = useContext(TeamContext);
  const activeSubscription = team?.settings?.shopifySubscription?.[0];
  const isEnabled =
    !!team?.settings.orderTracking?.billing?.invoiceBilling?.enabled;
  const teamTimezone = team?._shopify?.iana_timezone || "America/Los_Angeles";
  const now = getDateStartOfDayInTimezone(new Date(), teamTimezone);

  const subscriptionAlreadyStarted = useMemo(() => {
    const { enabled, startDate } = invoiceBilling.inputs;
    const alreadyEnabled = !enabled.changed && enabled.value;

    if (!alreadyEnabled) {
      return false;
    }

    const startDateIsTodayOrBefore =
      !!startDate.value &&
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <=
        now.getTime();

    if (startDateIsTodayOrBefore) {
      return true;
    }

    return false;
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamTimezone]);

  const shouldDisableToggle = useMemo(() => {
    const enabled = invoiceBilling.inputs.enabled;
    const startDate = invoiceBilling.inputs.startDate;
    const currentlyEnabled = enabled.value;
    const startDateIsInPast =
      !!startDate.value &&
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
        now.getTime();

    return currentlyEnabled && startDateIsInPast;
  }, [
    invoiceBilling.inputs.enabled,
    invoiceBilling.inputs.startDate,
    now,
    teamTimezone,
  ]);

  useEffect(() => {
    const startDate = invoiceBilling.inputs.startDate;
    const startDateIsUpdatedToThePast =
      invoiceBilling.inputs.startDateIsUpdatedToThePast;

    if (!startDate.changed || !startDate.value) {
      if (startDateIsUpdatedToThePast.value) {
        startDateIsUpdatedToThePast.setValue(false);
      }
      return;
    }

    const startDateIsInThePast =
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
      getDateStartOfDayInTimezone(now, teamTimezone).getTime();

    if (startDateIsUpdatedToThePast.value !== startDateIsInThePast) {
      startDateIsUpdatedToThePast.setValue(startDateIsInThePast);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceBilling, teamTimezone]);

  const invoiceBillingSettings = (
    <>
      <RedoToggle
        description="Automatically renew subscription at the end of each billing period"
        disabled={!isAuthorized}
        label="Renew when billing period ends"
        setValue={invoiceBilling.inputs.renewOnPeriodEnd.setValue}
        size="sm"
        value={invoiceBilling.inputs.renewOnPeriodEnd.value}
      />
      <LabeledInput
        description="We will charge the merchant their first period fee on this date."
        label="Start date"
      >
        <DateInput
          disabled={!isAuthorized || subscriptionAlreadyStarted}
          value={
            invoiceBilling.inputs.startDate.value
              ? (() => {
                  // Convert stored date (midnight in merchant timezone) back to a local date for display
                  const storedDate = invoiceBilling.inputs.startDate.value;
                  const zonedDateTime = Temporal.Instant.fromEpochMilliseconds(
                    storedDate.getTime(),
                  ).toZonedDateTimeISO(teamTimezone);
                  // Create a date in local timezone that represents the same calendar day
                  return new Date(
                    zonedDateTime.year,
                    zonedDateTime.month - 1,
                    zonedDateTime.day,
                  );
                })()
              : null
          }
          valueChange={(date) => {
            if (!date) {
              invoiceBilling.inputs.startDate.setValue(date);
            } else {
              invoiceBilling.inputs.startDate.setValue(
                getDateStartOfDayInTimezone(date, teamTimezone),
              );
            }
          }}
        />
      </LabeledInput>
      <FormRadioGroup
        disabled={!isAuthorized}
        input={invoiceBilling.inputs.period}
        label="Billing period"
        optionLabel={(option) => {
          return billingPeriodToFriendlyUpper[option];
        }}
        options={Object.values(BillingPeriod)}
      >
        How often to bill for the monthly credit allowance
      </FormRadioGroup>
      <FormRadioGroup
        disabled={!isAuthorized}
        input={invoiceBilling.inputs.paymentProvider}
        label="Payment provider"
        optionLabel={(option) => {
          switch (option) {
            case PaymentProviderType.SHOPIFY:
              return "Shopify";
            case PaymentProviderType.STRIPE:
              return "Stripe";
            default:
              assertNever(option);
          }
        }}
        options={Object.values(PaymentProviderType)}
      >
        Select which payment provider to use for billing
      </FormRadioGroup>
      {invoiceBilling.inputs.paymentProvider.value ===
        PaymentProviderType.STRIPE &&
        !teamGetPaymentMethod(team?.stripe ?? undefined) && (
          <Text fontSize="xs" textColor="warning">
            Team has no stripe payment method configured. Billing will fail
            until the team adds a payment method.
          </Text>
        )}
      <RedoIncrementDecrement
        disabled={!isAuthorized}
        fullWidth
        label="Billing period fee (USD)"
        min={0}
        prefix="$"
        setValue={invoiceBilling.inputs.periodFeeUsd.setValue}
        step={0.000_001}
        value={invoiceBilling.inputs.periodFeeUsd.value}
      />
      <RedoToggle
        description="Enable order-based billing"
        disabled={!isAuthorized}
        label="Bill Per Order"
        setValue={
          invoiceBilling.inputs.perOrderSettings.inputs.enabled.setValue
        }
        size="sm"
        value={invoiceBilling.inputs.perOrderSettings.inputs.enabled.value}
      />
      {invoiceBilling.inputs.perOrderSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included orders per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perOrderSettings.inputs.includedOrders
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perOrderSettings.inputs.includedOrders.value
            }
          />
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Price per order (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perOrderSettings.inputs
                .overagePricePerOrderUsd.setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perOrderSettings.inputs
                .overagePricePerOrderUsd.value
            }
          />
        </Flex>
      )}

      <RedoToggle
        description="Enable per-shipment billing (requires Order Tracking)"
        disabled={
          !isAuthorized || !orderTrackingEnabled
            ? ((!orderTrackingEnabled
                ? "Order Tracking must be enabled to bill per shipment"
                : undefined) ?? true)
            : false
        }
        label="Bill Per Shipment"
        setValue={
          invoiceBilling.inputs.perShipmentSettings.inputs.enabled.setValue
        }
        size="sm"
        value={invoiceBilling.inputs.perShipmentSettings.inputs.enabled.value}
      />
      {invoiceBilling.inputs.perShipmentSettings.inputs.enabled.value &&
        orderTrackingEnabled && (
          <Flex dir="column" gap="xl" pl="xl">
            <RedoIncrementDecrement
              disabled={!isAuthorized}
              fullWidth
              label="Included shipments per period"
              min={0}
              setValue={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .includedShipments.setValue
              }
              step={1}
              value={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .includedShipments.value
              }
            />
            <RedoIncrementDecrement
              disabled={!isAuthorized}
              fullWidth
              label="Overage price per shipment (USD)"
              min={0}
              prefix="$"
              setValue={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .overagePricePerShipmentUsd.setValue
              }
              step={0.000_001}
              value={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .overagePricePerShipmentUsd.value
              }
            />
          </Flex>
        )}

      <RedoToggle
        description="Enable SMS/MMS billing"
        disabled={!isAuthorized}
        label="Bill Per SMS"
        setValue={invoiceBilling.inputs.perSmsSettings.inputs.enabled.setValue}
        size="sm"
        value={invoiceBilling.inputs.perSmsSettings.inputs.enabled.value}
      />
      {invoiceBilling.inputs.perSmsSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included SMS credits per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.includedCredits
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.includedCredits.value
            }
          />
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per SMS (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerSmsUsd
                .setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerSmsUsd
                .value
            }
          />
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per MMS (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerMmsUsd
                .setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerMmsUsd
                .value
            }
          />
          <RedoToggle
            description="Charge carrier fees"
            disabled={!isAuthorized}
            label="Charge for carrier fees"
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.chargeCarrierFees
                .setValue
            }
            size="sm"
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.chargeCarrierFees
                .value
            }
          />
        </Flex>
      )}

      <RedoToggle
        description="Enable email billing"
        disabled={!isAuthorized}
        label="Bill Per Email"
        setValue={
          invoiceBilling.inputs.perEmailSettings.inputs.enabled.setValue
        }
        size="sm"
        value={invoiceBilling.inputs.perEmailSettings.inputs.enabled.value}
      />
      {invoiceBilling.inputs.perEmailSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included emails per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perEmailSettings.inputs.includedEmails
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perEmailSettings.inputs.includedEmails.value
            }
          />
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per email (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perEmailSettings.inputs
                .overagePricePerEmailUsd.setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perEmailSettings.inputs
                .overagePricePerEmailUsd.value
            }
          />
        </Flex>
      )}
    </>
  );

  return (
    <Card title="Order Tracking Billing">
      <section className={(gridCss.grid, gridCss.gap1)}>
        <Flex className={gridCss.span6L} dir="column" gap="xl">
          {activeSubscription &&
            isEnabled &&
            team.settings.orderTracking?.billing?.invoiceBilling
              ?.paymentProvider === PaymentProviderType.SHOPIFY && (
              <div>
                <h3>Active Subscription</h3>
                <Text as="p">Name: {activeSubscription?.name}</Text>
                <Text as="p">Status: {activeSubscription?.status}</Text>
              </div>
            )}
          <RedoToggle
            disabled={!isAuthorized || shouldDisableToggle}
            label="Enabled"
            setValue={invoiceBilling.inputs.enabled.setValue}
            size="sm"
            value={invoiceBilling.inputs.enabled.value}
          />

          {invoiceBilling.inputs.enabled.value && (
            <Flex dir="column" gap="xl">
              {invoiceBillingSettings}
            </Flex>
          )}
        </Flex>
      </section>
    </Card>
  );
});
